import { useState, useEffect, useRef } from 'react';
import { generateSignedUrl } from '../utils/audioUtils';
import { cleanupVideoElement, cleanupAudioElement } from '../utils/videoCleanup';

interface UseVideoPlayerStateProps {
  audioUrlParam: string | null;
  videoUrlParam: string | null;
  project: any;
}

export const useVideoPlayerState = ({
  audioUrlParam,
  videoUrlParam,
  project
}: UseVideoPlayerStateProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaCurrentTime, setMediaCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [hasVideoAudio, setHasVideoAudio] = useState<boolean>(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Track if we've initialized to prevent resets
  const hasInitialized = useRef(false);

  // Update URL when params or project changes
  useEffect(() => {
    let finalAudioUrl = audioUrlParam;
    let finalVideoUrl = videoUrlParam;

    if (!finalAudioUrl) {
      finalAudioUrl = project?.originalAudioUrl || project?.finalAudio || "";
    }

    if (!finalVideoUrl) {
      finalVideoUrl = project?.originalVideoUrl || "";
    }

    // Detect video-only mode (has video but no audio)
    if (finalVideoUrl && !finalAudioUrl) {
      hasInitialized.current = true;
      setHasVideoAudio(true);
      setVideoUrl(finalVideoUrl);
      setAudioUrl(null);
    } else if (finalAudioUrl) {
      hasInitialized.current = true;
      // Standard audio mode
      setHasVideoAudio(false);
      setVideoUrl(null);
      
      generateSignedUrl(finalAudioUrl)
        .then((signedUrl) => {
          console.log("🎵 Signed audio URL created:", { 
            original: finalAudioUrl, 
            signed: signedUrl 
          });
          setAudioUrl(signedUrl);
        })
        .catch((error) => {
          console.error("Failed to create signed audio URL:", error);
          setAudioUrl(finalAudioUrl);
        });
    } else {
      // No audio or video in params/project
      // Only reset if:
      // 1. Project is actually loaded (not undefined/null) - prevents premature reset during loading
      // 2. We haven't already initialized - prevents losing URLs from previous renders
      if (project && !hasInitialized.current) {
        setAudioUrl(null);
        setVideoUrl(null);
        setHasVideoAudio(false);
      }
    }
  }, [audioUrlParam, videoUrlParam, project]);

  // Sync audio element with audioUrl
  useEffect(() => {
    if (audioUrl && audioRef.current && !audioRef.current.src) {
      audioRef.current.src = audioUrl;
    }
  }, [audioUrl]);

  // Sync audio currentTime with state
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const diff = Math.abs(audioRef.current.currentTime - currentTime);
      if (diff > 0.1) {
        audioRef.current.currentTime = currentTime;
      }
    }
  }, [currentTime, audioUrl]);

  const handleAudioTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const time = e.currentTarget.currentTime;
    setCurrentTime(time);
    setMediaCurrentTime(time);
  };

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    setDuration(e.currentTarget.duration);
  };

  const handleSeek = (time: number) => {
    setMediaCurrentTime(time);
    setCurrentTime(time);
    if (audioRef.current && audioUrl) {
      audioRef.current.currentTime = time;
    }
  };

  const handleTimeUpdate = (time: number) => {
    setCurrentTime(time);
    setMediaCurrentTime(time);
  };

  const handleDurationChange = (newDuration: number) => {
    setDuration(newDuration);
  };

  const handleVideoPlay = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play().catch(err => {
        console.warn("Error playing audio:", err);
      });
    }
  };

  const handleVideoPause = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.pause();
    }
  };

  // ✅ CRITICAL: Cleanup video and audio elements on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up video and audio elements...');
      
      // Cleanup video element
      if (videoRef.current) {
        cleanupVideoElement(videoRef.current);
      }
      
      // Cleanup audio element
      if (audioRef.current) {
        cleanupAudioElement(audioRef.current);
      }
    };
  }, []);

  return {
    currentTime,
    setCurrentTime,
    mediaCurrentTime,
    setMediaCurrentTime,
    duration,
    setDuration,
    audioUrl,
    videoUrl,
    hasVideoAudio,
    audioRef,
    videoRef,
    handleAudioTimeUpdate,
    handleLoadedMetadata,
    handleSeek,
    handleTimeUpdate,
    handleDurationChange,
    handleVideoPlay,
    handleVideoPause
  };
};
